import React from "react";
import IndustryPage from "../../components/marketing/IndustryPage";
import { GraduationCap, FileText, MessageSquare, CalendarCheck, BookOpen, Mail, Sparkles, Phone } from "lucide-react";

export default function Education() {
  return (
    <IndustryPage
      slug="education"
      industry="Education"
      Icon={GraduationCap}
      accent="#8B5CF6"
      seoTitle="AI Admissions Assistant for Schools, Colleges & EdTech Platforms"
      seoDescription="HelloSir.ai is the AI admissions assistant for schools, colleges, universities and online course providers. Captures admissions enquiries, answers course questions, books open days and walks applicants through enrolment 24/7 — multilingual on chat, web voice and phone."
      seoKeywords={[
        "AI admissions chatbot", "AI for schools", "AI for universities",
        "edtech chatbot", "AI course advisor", "student enrolment AI",
        "AI for online course platforms", "multilingual education chatbot",
        "AI for colleges", "AI for tutoring centres", "AI for training providers",
      ]}
      hero={{
        headline: "Turn every visitor into an enrolled student.",
        sub: "From first-touch course question to fully captured application, HelloSir.ai walks prospective students through admissions in their own language — even at 2am on a Sunday.",
      }}
      painPoints={[
        { Icon: Mail,         title: "Admissions inbox overload", desc: "The same 30 questions about deadlines, fees and entry requirements drown your team." },
        { Icon: Phone,        title: "Parents call after hours",  desc: "Working parents reach out evenings & weekends, when admissions is closed." },
        { Icon: FileText,     title: "Application abandonment",    desc: "Most prospects start an application form and never finish it." },
      ]}
      capabilities={[
        { Icon: BookOpen,     title: "Course advisor",        desc: "Recommends programs based on goals, prior education and interests — pulled live from your prospectus." },
        { Icon: FileText,     title: "Entry requirements",    desc: "Explains required grades, IELTS / TOEFL scores and document checklists per program." },
        { Icon: CalendarCheck, title: "Books open days",       desc: "Synced to your campus calendar — confirms slots over chat, voice or phone." },
        { Icon: MessageSquare, title: "Application walk-through", desc: "Guides applicants step-by-step through your online form, with friendly nudges." },
        { Icon: Sparkles,     title: "Scholarship finder",    desc: "Matches applicants to scholarships and bursaries they're eligible for." },
        { Icon: GraduationCap, title: "20+ languages",         desc: "Serve international students in Arabic, Chinese, French, Hindi, Spanish and more." },
      ]}
      sampleChats={[
        { q: "I have IELTS 6.5, A-levels BBB. Can I apply for your BSc Computer Science?",
          a: "Yes — your IELTS meets the 6.0 minimum and BBB meets our typical offer of BBB. Application deadline is 30 June; portfolio not required. Want me to start your application now?" },
        { q: "Are there scholarships for international students?",
          a: "Three options match your profile: ① International Excellence Scholarship — £4,000. ② Women-in-Tech Award — £2,500. ③ Early-bird Discount — 10% off if you accept by 30 April. Want details for any?" },
        { q: "When is the next open day?",
          a: "Saturday 9 March, 10am–3pm. I can register you and email a campus map. Shall I book it?" },
      ]}
      sources={[
        "Your prospectus & course pages",
        "Live admissions deadlines",
        "Scholarship database",
        "Open-day calendar",
      ]}
      integrations={[
        "Hubspot", "Salesforce Education Cloud", "Microsoft Dynamics",
        "Google Calendar", "Outlook", "Mailchimp", "Twilio SMS",
        "Custom student-information systems (SIS)", "Zapier",
      ]}
      metrics={[
        { value: "+34%", label: "Increase in completed applications" },
        { value: "−58%", label: "Drop in repetitive admissions email" },
        { value: "24/7", label: "Multilingual coverage" },
        { value: "<10s", label: "Response time on chat" },
      ]}
      faqs={[
        { q: "Can it really answer course-specific questions?",
          a: "Yes. Once trained on your prospectus PDFs and course pages, it answers in your tone of voice with cited sources." },
        { q: "Does it support international students?",
          a: "20+ languages live. It also understands grade equivalences (e.g. Indian CBSE, UAE high school, US GPA) when you upload your conversion tables." },
        { q: "Will it replace admissions counsellors?",
          a: "No — it handles routine intake so counsellors spend time on high-intent applicants. Complex cases are routed to a human." },
        { q: "Can it integrate with our student information system?",
          a: "Yes — via REST/webhook to most modern SIS platforms, or Zapier as a fallback." },
      ]}
    />
  );
}
